import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Signup = ({ setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [signupsPaused, setSignupsPaused] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  // OTP step
  const [step, setStep] = useState('form'); // 'form' | 'verify'
  const [signupToken, setSignupToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpEmail, setOtpEmail] = useState('');

  useEffect(() => {
    axios.get(`${API}/settings/signups`)
      .then(res => setSignupsPaused(!!res.data.paused))
      .catch(() => setSignupsPaused(false))
      .finally(() => setCheckingStatus(false));
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/signup-init`, formData);
      setSignupToken(response.data.signup_token);
      setOtpEmail(response.data.email);
      setStep('verify');
      toast.success(`Code sent to ${response.data.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otpCode)) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup-verify`, {
        signup_token: signupToken,
        code: otpCode,
      });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      toast.success('Account created! Check your email for your US address.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const startOver = () => {
    setStep('form');
    setSignupToken('');
    setOtpCode('');
    setOtpEmail('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center auth-bg px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src="/dac-logo.png" alt="DAC Shipping" className="h-16 w-auto object-contain" />
          </div>
          <CardTitle className="text-3xl font-bold">Create Account</CardTitle>
          <CardDescription>Get your US shipping address in seconds</CardDescription>
        </CardHeader>
        <CardContent>
          {signupsPaused ? (
            <div
              data-testid="signup-paused-notice"
              className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-center space-y-3"
            >
              <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center text-2xl">⏸</div>
              <h3 className="text-lg font-bold text-amber-900">New Signups Temporarily Paused</h3>
              <p className="text-sm text-amber-800">
                We've hit capacity and are not accepting new customers at the moment. Existing customers can still log in and use their accounts normally.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row gap-2 justify-center">
                <a
                  href="https://wa.me/15926350592?text=Hello%2C%20I%27d%20like%20to%20be%20notified%20when%20DAC%20Shipping%20signups%20reopen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-semibold transition"
                  data-testid="signup-paused-whatsapp"
                >
                  Ask on WhatsApp
                </a>
                <Link to="/login">
                  <Button variant="outline" className="w-full sm:w-auto">Existing Customer Login</Button>
                </Link>
              </div>
            </div>
          ) : step === 'verify' ? (
            <form onSubmit={handleVerify} className="space-y-5" data-testid="signup-otp-form">
              <div className="text-center space-y-2">
                <div className="mx-auto h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">📧</div>
                <h3 className="text-lg font-bold text-slate-900">Check your email</h3>
                <p className="text-sm text-slate-600">
                  We've sent a 6-digit code to <span className="font-semibold text-slate-900">{otpEmail}</span>. Enter it below to finish creating your account.
                </p>
                <p className="text-xs text-slate-500">Code expires in 10 minutes. If you close this page, you'll need to start over.</p>
              </div>
              <div>
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  data-testid="signup-otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.6em] font-mono"
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="signup-verify-btn"
                className="w-full bg-orange-500 hover:bg-orange-600"
                disabled={loading || otpCode.length !== 6}
              >
                {loading ? 'Verifying…' : 'Verify & Create Account'}
              </Button>
              <button
                type="button"
                onClick={startOver}
                className="w-full text-sm text-slate-500 hover:text-slate-700 underline"
                data-testid="signup-start-over-btn"
              >
                Use a different email
              </button>
            </form>
          ) : (
            <>
          <form onSubmit={handleSignup} className="space-y-4">
            <fieldset disabled={checkingStatus} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                data-testid="signup-name-input"
                name="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="signup-email-input"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                data-testid="signup-phone-input"
                name="phone"
                type="tel"
                placeholder="592-xxx-xxxx"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="address">Address (Guyana)</Label>
              <Input
                id="address"
                data-testid="signup-address-input"
                name="address"
                type="text"
                placeholder="Your address in Guyana"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="signup-password-input"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>
            <Button
              data-testid="signup-submit-btn"
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={loading}
            >
              {loading ? 'Sending verification code…' : 'Continue · Send Verification Code'}
            </Button>
            </fieldset>
          </form>
            </>
          )}
          <div className="mt-4 text-center text-sm">
            <span className="text-slate-600">Already have an account? </span>
            <Link to="/login" className="text-orange-500 hover:text-orange-600 font-semibold">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Signup;